import React, { useState } from 'react'
import classNames from 'classnames'
import { Header, Button, Icon, Stack, Text } from '@crossfit/ui'

import Modal from 'components/Modal'
import { useMediaQuery } from 'hooks/useMediaQuery'
import style from './style.module.css'

const PERMISSIONS: Permission[] = [
  { text: 'Can access Dashboard', roles: [true, true, true] },
  { text: 'Can access Programming', roles: [true, true, true] },
  { text: 'Can access Marketing Kit', roles: [true, true, true] },
  { text: 'Can access Playbook', roles: [true, true, true] },
  { text: 'Can view coaches & staff', roles: [true, true, true] },
  { text: 'Can remove self from coaches & staff', roles: [true, true, false] },
  { text: 'Can access Contact Info', roles: [false, true, true] },
  { text: 'Can access Directory Page', roles: [false, true, true] },
  { text: 'Can invite and remove coaches & staff', roles: [false, true, true] },
  { text: 'Can access Roundtables', roles: [false, false, true] },
  { text: 'Can access Starter Kit', roles: [false, false, true] },
  { text: 'Can access Payment Center', roles: [false, false, true] },
  { text: 'Can access Partner Network', roles: [false, true, true] },
]

type Permission = { text: string; roles: [boolean, boolean, boolean] }

type Props = {
  onClose: () => void
}

type PermissionListProps = {
  isMobile: boolean
  children: React.ReactNode
  roleIndex: number
  setRoleIndex: (i: number) => void
}

function PermissionList({
  isMobile,
  children,
  roleIndex,
  setRoleIndex,
}: PermissionListProps) {
  return isMobile ? (
    <Stack mb={10} spacing={6}>
      <nav className={style.tabs}>
        <button
          className={classNames(style.tab, {
            [style.active]: roleIndex === 0,
          })}
          onClick={() => setRoleIndex(0)}
        >
          Coach
        </button>
        <button
          className={classNames(style.tab, {
            [style.active]: roleIndex === 1,
          })}
          onClick={() => setRoleIndex(1)}
        >
          Manger
        </button>
        <button
          className={classNames(style.tab, {
            [style.active]: roleIndex === 2,
          })}
          onClick={() => setRoleIndex(2)}
        >
          Owner
        </button>
      </nav>
      {children}
    </Stack>
  ) : (
    <table className={classNames('cf-table', 'cf-mb-8')}>
      <thead>
        <tr>
          <th>Permission</th>
          <th>Coach</th>
          <th>Manager</th>
          <th>Owner</th>
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  )
}

type PermissionListItemProps = {
  permission: Permission
  isMobile: boolean
  roleIndex: number
}

function PermissionListItem({
  isMobile,
  permission,
  roleIndex,
}: PermissionListItemProps) {
  return isMobile ? (
    <div className={style.item}>
      <div className={style.label}>{permission.text}</div>

      {permission.roles[roleIndex] ? (
        <Icon name='check' className={style.checkIcon} />
      ) : (
        <Icon name='close' className={style.closeIcon} />
      )}
    </div>
  ) : (
    <tr>
      <td>{permission.text}</td>
      {permission.roles.map((role, index) => (
        <td className={style.tableCellWithIcon} key={index}>
          {role ? (
            <Icon name='check' className={style.checkIcon} />
          ) : (
            <Icon name='close' className={style.closeIcon} />
          )}
        </td>
      ))}
    </tr>
  )
}

function TeamRolesModal({ onClose }: Props) {
  const isMobile = useMediaQuery(`(max-width: 768px)`)
  const [roleIndex, setRoleIndex] = useState(0)

  return (
    <Modal
      onClose={onClose}
      header={
        <Header size='small' as='h3'>
          Coaches & Staff Roles
        </Header>
      }
    >
      <Text mb={8}>
        Coaches & Staff are made up of 1 owner, 1 manager (optional), and an
        unlimited number of Coaches. All coaches & staff must have an active
        CF-L1 certificate.
      </Text>

      <PermissionList
        isMobile={isMobile}
        roleIndex={roleIndex}
        setRoleIndex={setRoleIndex}
      >
        {PERMISSIONS.map((permission, index) => (
          <PermissionListItem
            key={index}
            permission={permission}
            isMobile={isMobile}
            roleIndex={roleIndex}
          />
        ))}
      </PermissionList>

      <Stack direction='horizontal' style={{ justifyContent: 'flex-end' }}>
        <Button onClick={onClose} variant='secondary'>
          Close
        </Button>
      </Stack>
    </Modal>
  )
}

export default TeamRolesModal
